import React from 'react';
import { License } from '@/types/broker-registration.types';
import { FormField } from '../FormField';
import { TextInput } from '../TextInput';
import { CheckboxGroup } from '../CheckboxGroup';
import {
  CANADIAN_PROVINCES,
  US_STATES,
  EUROPEAN_COUNTRIES,
} from '@/constants/broker-registration.constants';

interface LicenseBlockProps {
  license: License;
  index: number;
  onUpdate: (index: number, field: keyof License, value: string | string[]) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  errors: Record<string, string>;
  clearError: (field: string) => void;
}

export function LicenseBlock({
  license,
  index,
  onUpdate,
  onRemove,
  canRemove,
  errors,
  clearError,
}: LicenseBlockProps) {
  const toggleJurisdiction = (jurisdiction: string) => {
    const newJurisdictions = license.jurisdictions.includes(jurisdiction)
      ? license.jurisdictions.filter((j) => j !== jurisdiction)
      : [...license.jurisdictions, jurisdiction];
    onUpdate(index, 'jurisdictions', newJurisdictions);
  };

  const toggleAllInRegion = (jurisdictions: string[]) => {
    const allSelected = jurisdictions.every((j) => license.jurisdictions.includes(j));
    if (allSelected) {
      // Remove all from region
      const newJurisdictions = license.jurisdictions.filter((j) => !jurisdictions.includes(j));
      onUpdate(index, 'jurisdictions', newJurisdictions);
    } else {
      // Add all from region
      const newJurisdictions = Array.from(
        new Set([...license.jurisdictions, ...jurisdictions])
      );
      onUpdate(index, 'jurisdictions', newJurisdictions);
    }
  };

  const canadianJurisdictions = CANADIAN_PROVINCES.map((p) => p.value);
  const usJurisdictions = US_STATES.map((s) => s.value);
  const europeanJurisdictions = EUROPEAN_COUNTRIES.map((c) => c.value);

  const allCanadaSelected =
    canadianJurisdictions.length > 0 &&
    canadianJurisdictions.every((j) => license.jurisdictions.includes(j));
  const allUSSelected =
    usJurisdictions.length > 0 &&
    usJurisdictions.every((j) => license.jurisdictions.includes(j));
  const allEUSelected =
    europeanJurisdictions.length > 0 &&
    europeanJurisdictions.every((j) => license.jurisdictions.includes(j));

  return (
    <div className="p-4 border border-slate-200 rounded-lg mb-4 bg-slate-50 relative">
      <h3 className="text-md font-semibold mb-3 text-[#1D314D]">
        License/Permit {index + 1}
      </h3>

      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="absolute top-3 right-3 text-red-400 hover:text-red-600 transition duration-150"
          aria-label="Remove license"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-3 0h14"
            ></path>
          </svg>
        </button>
      )}

      <FormField
        label="License/permit details (Ex: CPA - QC, JD - NY Bar)"
        error={errors[`license_${index}_details`]}
        required
      >
        <textarea
          value={license.details}
          onChange={(e) => {
            onUpdate(index, 'details', e.target.value);
            clearError(`license_${index}_details`);
          }}
          placeholder="License type and issuing organization"
          rows={2}
          className={`w-full px-3 py-2.5 rounded-lg bg-white border text-[#1D314D] placeholder:text-slate-400 focus:ring-2 focus:ring-[#CEAB65]/50 focus:border-[#CEAB65] transition duration-150 ${
            errors[`license_${index}_details`] ? 'border-red-500' : 'border-slate-300'
          }`}
        />
      </FormField>

      <FormField
        label="Active license number"
        error={errors[`license_${index}_number`]}
        required
      >
        <TextInput
          value={license.number}
          onChange={(value) => {
            onUpdate(index, 'number', value);
            clearError(`license_${index}_number`);
          }}
          placeholder="Reference number or ID"
          error={!!errors[`license_${index}_number`]}
        />
      </FormField>

      <div className="mt-4">
        <label className="block text-sm font-medium text-[#1D314D] mb-2">
          Covered Jurisdictions (where you can legally practice)
        </label>
        <p className="text-xs text-slate-500 mb-2">
          Jurisdictions are not mandatory as not all can be listed
        </p>

        {/* Canada */}
        <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
          <label className="flex items-center mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allCanadaSelected}
              onChange={() => toggleAllInRegion(canadianJurisdictions)}
              className="h-4 w-4 text-[#CEAB65] rounded border-slate-300 focus:ring-[#CEAB65]"
            />
            <span className="ml-2 font-semibold text-[#1D314D]">Canada</span>
          </label>
          <div className="ml-6">
            <CheckboxGroup
              values={license.jurisdictions}
              onChange={(values) => onUpdate(index, 'jurisdictions', values)}
              options={CANADIAN_PROVINCES}
              columns={3}
              className="max-h-32 overflow-y-auto"
            />
          </div>
        </div>

        {/* United States */}
        <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
          <label className="flex items-center mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allUSSelected}
              onChange={() => toggleAllInRegion(usJurisdictions)}
              className="h-4 w-4 text-[#CEAB65] rounded border-slate-300 focus:ring-[#CEAB65]"
            />
            <span className="ml-2 font-semibold text-[#1D314D]">United States</span>
          </label>
          <div className="ml-6">
            <CheckboxGroup
              values={license.jurisdictions}
              onChange={(values) => onUpdate(index, 'jurisdictions', values)}
              options={US_STATES}
              columns={3}
              className="max-h-32 overflow-y-auto"
            />
          </div>
        </div>

        {/* Europe */}
        <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
          <label className="flex items-center mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allEUSelected}
              onChange={() => toggleAllInRegion(europeanJurisdictions)}
              className="h-4 w-4 text-[#CEAB65] rounded border-slate-300 focus:ring-[#CEAB65]"
            />
            <span className="ml-2 font-semibold text-[#1D314D]">Europe (Selective Zone)</span>
          </label>
          <div className="ml-6">
            <CheckboxGroup
              values={license.jurisdictions}
              onChange={(values) => onUpdate(index, 'jurisdictions', values)}
              options={EUROPEAN_COUNTRIES}
              columns={2}
            />
          </div>
        </div>

        {/* Other Jurisdictions */}
        <FormField label="Other Jurisdictions (specify)">
          <TextInput
            value={license.otherJurisdictions}
            onChange={(value) => onUpdate(index, 'otherJurisdictions', value)}
            placeholder="E.g.: Mexico, Australia, etc."
          />
        </FormField>
      </div>
    </div>
  );
}
